/**
 * Token-rect + char-span → redaction-rect mapping. Direct port of
 * `SpanMapper.kt`, including the `verticallyOverlaps + slop` rule that
 * merges adjacent same-line tokens into a single rect.
 */

import type { OcrTokenBox } from "./ocr.js";
import type { PiiSpan } from "./pii.js";
import type { RedactionRect } from "./renderer.js";

const PADDING_PX = 4;
const MERGE_SLOP_PX = 8;

interface RawRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export function mapSpansToRects(
  tokens: readonly OcrTokenBox[],
  spans: readonly PiiSpan[],
  bitmapWidth: number,
  bitmapHeight: number,
): RedactionRect[] {
  const out: RedactionRect[] = [];
  for (const span of spans) {
    const tokenRects = tokens
      .filter((t) => t.start < span.endExclusive && t.endExclusive > span.start)
      .map((t) => ({ ...t.rect }));
    const merged = mergeAdjacent(tokenRects, MERGE_SLOP_PX);
    for (const r of merged) {
      out.push({
        rect: clamp(pad(r, PADDING_PX), bitmapWidth, bitmapHeight),
        source: "AUTO",
        fillMode: "SOLID",
        fillColor: 0xff000000,
        label: span.label,
      });
    }
  }
  return out;
}

function mergeAdjacent(rects: RawRect[], slop: number): RawRect[] {
  if (rects.length === 0) return [];
  const sorted = [...rects].sort((a, b) => a.top - b.top || a.left - b.left);
  const merged: RawRect[] = [];
  for (const r of sorted) {
    const last = merged[merged.length - 1];
    if (
      last !== undefined &&
      verticallyOverlaps(last, r) &&
      intersects({ ...last, right: last.right + slop }, r)
    ) {
      last.left = Math.min(last.left, r.left);
      last.top = Math.min(last.top, r.top);
      last.right = Math.max(last.right, r.right);
      last.bottom = Math.max(last.bottom, r.bottom);
    } else {
      merged.push({ ...r });
    }
  }
  return merged;
}

function verticallyOverlaps(a: RawRect, b: RawRect): boolean {
  const overlap = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  const minH = Math.min(a.bottom - a.top, b.bottom - b.top);
  return overlap > minH / 2;
}

function intersects(a: RawRect, b: RawRect): boolean {
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

function pad(r: RawRect, px: number): RawRect {
  return { left: r.left - px, top: r.top - px, right: r.right + px, bottom: r.bottom + px };
}

function clamp(r: RawRect, w: number, h: number): RawRect {
  return {
    left: Math.max(0, r.left),
    top: Math.max(0, r.top),
    right: Math.min(w, r.right),
    bottom: Math.min(h, r.bottom),
  };
}
